import React, { Component } from "react"
import PropTypes from "prop-types"

import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

import {
    enumSingleGenericFilter,
    datePickerGenericFilter,
    textGenericFilter,
    // sorting functions
    enumLabelSort,
    enumOrderSort,
    enumValueSort,
} from "shared/search/containers/Filter/Filters"
import {
    enumGenericFilter,
    searchifySelectGenericFilter,
    toggleGenericFilter,
} from "shared/search/containers/Filter/FilterCreators"

import {
    ElectionFilterableListView,
    PoliticalCommitteeFilterableListView,
    PublicPacOrganizationFilterableListView,
    SupporterFilterableListView,
    OfficialFilterableWithoutCampaignFinanceListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"

import {
    ContactSelect,
    ElectionSelect,
    LedgerSettingsSelect,
    PoliticalCommitteeSelect,
    PublicPacOrganizationSelect,
    OfficialWithoutCampaignFinanceSelect,
} from "app/static/frontend/selects/containers/PieSelect"
import { DateListNoFutureSelect, LedgerAccountTypeSelect } from "app/static/frontend/selects/containers/EnumSelect"

export class TransactionDateListFilter extends Component {
    static defaultProps = {
        filterComponent: enumSingleGenericFilter,
        uniqueFilterId: "transactionDateListFilter",
        filterKey: "occurred_date_range",
        enumItems: DjangIO.app.events.models.DateRangeType.region_supported_items().filter(
            (item) =>
                ![
                    DjangIO.app.events.models.DateRangeType.future.value,
                    DjangIO.app.events.models.DateRangeType.next_month.value,
                    DjangIO.app.events.models.DateRangeType.next_week.value,
                ].includes(item.value),
        ),
        sortBy: enumOrderSort,
        selectComponent: DateListNoFutureSelect,
        displayLabel: "Date of Transaction",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionTypeFilter extends Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "transactionTypeFilter",
        filterKey: "transaction_type__in",
        enumItems: (function getEnumItems() {
            const cannotViewReceipts = isFeatureEnabled("ff_pac_permissions_one") && !Userdata.can_view_pac_receipts
            if (cannotViewReceipts) {
                const excludedTypes = new Set([
                    DjangIO.app.ledger.types.TransactionType.money_received.value,
                    DjangIO.app.ledger.types.TransactionType.refund_received.value,
                    DjangIO.app.ledger.types.TransactionType.bank_transfer.value,
                ])

                return DjangIO.app.ledger.types.TransactionType.region_supported_items().filter(
                    (transactionType) => !excludedTypes.has(transactionType.value),
                )
            }

            const cannotViewDisbursements =
                isFeatureEnabled("ff_pac_permissions_one") && !Userdata.can_view_pac_disbursements
            if (cannotViewDisbursements) {
                const excludedTypes = new Set([
                    DjangIO.app.ledger.types.TransactionType.money_given.value,
                    DjangIO.app.ledger.types.TransactionType.refund_given.value,
                    DjangIO.app.ledger.types.TransactionType.bank_transfer.value,
                ])

                return DjangIO.app.ledger.types.TransactionType.region_supported_items().filter(
                    (transactionType) => !excludedTypes.has(transactionType.value),
                )
            }

            if (isFeatureEnabled("ff_pac_permissions_one")) {
                return DjangIO.app.ledger.types.TransactionType.region_supported_items().filter(
                    (transactionType) =>
                        transactionType.value !== DjangIO.app.ledger.types.TransactionType.bank_transfer.value,
                )
            }

            return DjangIO.app.ledger.types.TransactionType.region_supported_items()
        })(),
        displayKey: "pac_label",
        sortBy: enumValueSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export const TransactionLedgerSettingsFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent {...props} />
}
TransactionLedgerSettingsFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    searchifyFLV: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
TransactionLedgerSettingsFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "transactionLedgerSettingsFilter",
    filterKey: "for_ledger_settings",
    dataTypeLabel: "Ledger Settings",
    selectComponent: LedgerSettingsSelect,
}

export const TransactionSupporterFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={SupporterFilterableListView} {...props} />
}
TransactionSupporterFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    searchifyFLV: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
TransactionSupporterFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "transactionSupporterFilter",
    filterKey: "transaction_supporter",
    dataTypeLabel: "Contacts",
    selectComponent: ContactSelect,
}

export class TransactionOfficialFilter extends Component {
    static get defaultProps() {
        return {
            filterComponent: searchifySelectGenericFilter,
            uniqueFilterId: "transactionOfficialFilter",
            filterKey: "transaction_person",
            dataTypeLabel: "Official",
            permissions: [DjangIO.app.models.PermissionType.legislators],
            searchifyFLV: OfficialFilterableWithoutCampaignFinanceListView,
            selectComponent: OfficialWithoutCampaignFinanceSelect,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export const TransactionPublicOrganizationFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={PublicPacOrganizationFilterableListView} {...props} />
}
TransactionPublicOrganizationFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    searchifyFLV: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
TransactionPublicOrganizationFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "transactionPublicOrganizationFilter",
    filterKey: "transaction_public_organization",
    dataTypeLabel: "Organization",
    selectComponent: PublicPacOrganizationSelect,
}

export const TransactionPoliticalCommitteeFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={PoliticalCommitteeFilterableListView} {...props} />
}
TransactionPoliticalCommitteeFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    searchifyFLV: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
TransactionPoliticalCommitteeFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "transactionPoliticalCommitteeFilter",
    filterKey: "transaction_political_committee",
    dataTypeLabel: "Political Committee",
    selectComponent: PoliticalCommitteeSelect,
}

export const TransactionElectionFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={ElectionFilterableListView} {...props} />
}
TransactionElectionFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    searchifyFLV: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
TransactionElectionFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "transactionElectionFilter",
    filterKey: "transaction_election",
    dataTypeLabel: "Election",
    selectComponent: ElectionSelect,
}

export class TransactionAccountTypeFilter extends Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "transactionAccountTypeFilter",
        filterKey: "transaction_account_type",
        enumItems: DjangIO.app.ledger.types.LedgerAccountType.region_supported_items().filter(
            (ledgerAccount) =>
                ![
                    DjangIO.app.ledger.types.LedgerAccountType.checking_account.value,
                    DjangIO.app.ledger.types.LedgerAccountType.savings_account.value,
                ].includes(ledgerAccount.value) && ledgerAccount.is_primary,
        ),
        sortBy: enumLabelSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionAccountTypeSelectFilter extends Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "transactionAccountTypeFilter",
        filterKey: "transaction_account_type",
        enumItems: DjangIO.app.ledger.types.LedgerAccountType.region_supported_items().filter(
            (ledgerAccount) =>
                ![
                    DjangIO.app.ledger.types.LedgerAccountType.checking_account.value,
                    DjangIO.app.ledger.types.LedgerAccountType.savings_account.value,
                ].includes(ledgerAccount.value),
        ),
        selectComponent: LedgerAccountTypeSelect,
        displayLabel: "Other Transaction Types",
        sortBy: enumLabelSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionIsReconciledFilter extends Component {
    static defaultProps = {
        filterComponent: toggleGenericFilter,
        uniqueFilterId: "transactionIsReconciledFilter",
        filterKey: "is_reconciled",
        trueText: "Reconciled",
        falseText: "Not Reconciled",
        displayLabel: "Is Reconciled",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionIsVoidFilter extends Component {
    static defaultProps = {
        filterComponent: toggleGenericFilter,
        uniqueFilterId: "transactionIsVoidFilter",
        filterKey: "transaction_is_void",
        trueText: "Void",
        falseText: "Not Void",
        displayLabel: "Is Void",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionIsVoidDatePickerStartFilter extends Component {
    static defaultProps = {
        filterComponent: datePickerGenericFilter,
        uniqueFilterId: "transactionIsVoidDatePickerStartFilter",
        filterKey: "void_date__gt",
        displayLabel: "Void Start Date",
        helpText: "Transactions voided after this date. Excludes date selected.",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionIsVoidDatePickerEndFilter extends Component {
    static defaultProps = {
        filterComponent: datePickerGenericFilter,
        uniqueFilterId: "transactionIsVoidDatePickerEndFilter",
        filterKey: "void_date__lt",
        displayLabel: "Void End Date",
        helpText: "Transactions voided before this date. Excludes date selected.",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionIsMemoEntryFilter extends Component {
    static defaultProps = {
        filterComponent: toggleGenericFilter,
        uniqueFilterId: "transactionIsMemoEntryFilter",
        filterKey: "is_memo_entry",
        displayLabel: "Is Memo Only",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionSerialNumberFilter extends Component {
    static get defaultProps() {
        return {
            filterComponent: textGenericFilter,
            uniqueFilterId: "transactionSerialNumberFilter",
            filterKey: "transaction_serial_number",
            // placeholderText: "12345",
            displayLabel: "Search by FEC Filing Transaction ID",
            shouldShowFunc: () => Boolean(Userdata.logged_in_as_user),
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionPACCIdFilter extends Component {
    static get defaultProps() {
        return {
            filterComponent: textGenericFilter,
            uniqueFilterId: "transactionPACCIdFilter",
            filterKey: "transaction_pacc_id",
            // placeholderText: "12345",
            displayLabel: "Search by Transaction ID",
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class TransactionImportIdFilter extends Component {
    static get defaultProps() {
        return {
            filterComponent: textGenericFilter,
            uniqueFilterId: "transactionImportIdFilter",
            filterKey: "transaction_import_id",
            // placeholderText: "12345",
            displayLabel: "Search by Import ID",
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}
